/**
 * UX Lucky Game — board engine + direct Klaviyo reward claim + optional list signup.
 *
 * The reward goes to Klaviyo as the metric `lucky_game_voucher_claimed` with an explicit email
 * profile and the stable voucher id `voucher_<key>`; a Klaviyo flow turns that into the coupon.
 * The frontend never invents a discount code and never posts to Shopify. The list signup is a
 * second, independent request: a rejected one is logged, never shown, and never costs the guest
 * the voucher. A reward may carry a relative `weight`; as soon as one does, the slot is drawn
 * from those weights and the ball is steered into it instead of inheriting the board's edge-heavy
 * physics.
 *
 * Markup contract — every hook is queried inside the [data-ux-lucky-game] root, so several
 * instances can coexist on one page:
 *
 *   data-ux-lucky-game-klaviyo="…"       Klaviyo public API key; empty = no Klaviyo calls
 *   data-ux-lucky-game-once="0|1"        "0" = replayable; default = one drop per browser
 *   data-ux-lucky-game-open-delay="…"    seconds on a page before the popup opens itself; default 30
 *   data-ux-lucky-game-sticky="0|1"      "1" = the launch button was rendered and may be revealed;
 *                                 absent/"0" = never shown (Liquid omits it entirely by default)
 *   data-ux-lucky-game-sticky-hide="…"   "0" = keep the launch button after a play; default = hide it
 *                                 (ignored when data-ux-lucky-game-once="0", the game stays reachable)
 *   data-ux-lucky-game-subscribe="0|1"   "1" = also subscribe the submitted email; Liquid only sets
 *                                 it when a list ID is configured (see below)
 *   data-ux-lucky-game-list="…"          Klaviyo list ID the signup joins; empty = no signup
 *   data-ux-lucky-game-source="…"        custom_source written onto the consent record
 *   [data-ux-lucky-game-canvas]          <canvas>
 *   [data-ux-lucky-game-drop]            drop button, optional data-ux-lucky-game-label-locked
 *   [data-ux-lucky-game-result]          result line
 *   [data-ux-lucky-game-rewards-grid]    slot label container
 *   script[data-ux-lucky-game-rewards-json]  [{"key","label","weight"?,"note"?}, …] slot order =
 *                                 reward order; a numeric weight switches the board to weighted odds;
 *                                 `note` is the reward's condition line on the email step
 *   .ux-lucky-game-sticky                optional launch button wrapper, rendered hidden and only
 *                                 revealed once there is something to come back for
 *   [data-ux-lucky-game-sticky]          the button itself (data-ux-lucky-game-label-claimed), with
 *   [data-ux-lucky-game-sticky-label]    and [data-ux-lucky-game-sticky-badge] inside
 *   [data-ux-lucky-game-sticky-dismiss]  optional close chip; hides the widget in this browser
 *   [data-ux-lucky-game-modal]           data-ux-lucky-game-state="game|claim|claimed|success";
 *                                 no [hidden] attribute = open on load (the script's own delayed
 *                                 open is what normally unhides it)
 *   [data-ux-lucky-game-dialog] [data-ux-lucky-game-backdrop] [data-ux-lucky-game-close]
 *   [data-ux-lucky-game-panel="…"]       one per state: game board, email step, already claimed, sent
 *   .ux-lucky-game-burst                 firework layer created by the script when the ball lands
 *   [data-ux-lucky-game-reward-label]    filled with the won reward label, split into
 *                                 [data-ux-lucky-game-reward-main] ("AED 75") and a suffix ("off") so
 *                                 the stylesheet can size the amount; `title` carries the full label
 *   [data-ux-lucky-game-reward-amount]   filled with the amount alone (e.g. inside the submit label)
 *   [data-ux-lucky-game-reward-note]     filled with the reward's `note`, or the suffix without one
 *   [data-ux-lucky-game-whatsapp]        optional WhatsApp number: the whatsapp_number property, plus
 *                                 phone_number in a separate request when it parses to E.164
 *   [data-ux-lucky-game-dial]            optional <select> of dial codes (digits only) for that number
 *   [data-ux-lucky-game-form]            <form> or a wrapper containing one
 *   [data-ux-lucky-game-email]           required email input
 *   [data-ux-lucky-game-consent]         optional opt-out checkbox (ticked by default); unticking it
 *                                 drops the Klaviyo list signup, never the voucher claim
 *   [data-ux-lucky-game-error]
 */
(function () {
  'use strict';

  // Fallback when the markup carries no rewards JSON. The weights are inert: `weightedOdds` is
  // false without a declared JSON, so the board keeps its own physics.
  // The keys must match the Klaviyo flow's voucher_<key> branches.
  var DEFAULT_REWARDS = [
    { key: '30off', label: 'AED 30 off', weight: 1, note: 'off orders over AED 199' },
    { key: '50off', label: 'AED 50 off', weight: 1, note: 'off orders over AED 299' },
    { key: '75off', label: 'AED 75 off', weight: 1, note: 'off orders over AED 349' },
    { key: '20off', label: 'AED 20 off', weight: 1, note: 'off any order, no minimum' },
    { key: '40off', label: 'AED 40 off', weight: 1, note: 'off the first subscription order' }
  ];

  // 9 pegs on the bottom row, 46px apart, spans 368 of the 420px board: the pyramid reaches nearly
  // to the walls, which is what leaves no open corridor for the ball to slide down. A narrower field
  // (the previous 35px gap) gave the ball a clear run to a wall and the outer slots took ~46% each.
  // The height is only the fall corridor under the last peg row (row 7 sits at y=253): 340 left ~83px
  // of empty board below the pyramid, so the reward chips floated well clear of it. 300 keeps ~43px —
  // enough for the 7px ball to land and settle clear of the pegs — and cuts the dead space. Whatever
  // this is, the stylesheet's aspect-ratio on .ux-lucky-game-canvas must match it.
  var BOARD = {
    width: 420,
    height: 300,
    pegRadius: 4,
    rowGap: 34,
    pegGap: 46,
    startY: 15,
    rows: 8
  };

  // gravity (px/s²), restitution (0 = no bounce), tangential friction on peg contact, air drag
  // per second, and a speed cap so the ball cannot tunnel through pegs.
  // fixedStep is small on purpose: at 1/180 the ball moves up to 5.8px per step, enough to clear the
  // 12px vertical gap between two peg rows without ever registering the contact.
  var PHYSICS = {
    gravity: 980,
    restitution: 0.56,
    friction: 0.985,
    airDrag: 0.998,
    wallRestitution: 0.45,
    maxSpeed: 1050,
    fixedStep: 1 / 720
  };

  // Deflection floor: a ball landing dead centre on a peg has no tangential component, so the plain
  // reflection leaves it with vx ≈ 0 and it micro-bounces on that one peg instead of continuing down.
  // Any contact slower than MIN_NORMAL_SPEED is left to gravity; a real impact always gets a sideways
  // component. APEX_NUDGE breaks the same dead-centre geometry on the very first contact, where the
  // ball arrives exactly between the two apex pegs and the collision loop's array order used to decide
  // the whole drop (measured: 100% to the left peg).
  var CONTACT = {
    minDeflect: 14,
    minNormalSpeed: 40,
    apexNudge: 3.5
  };

  // Lateral steering, used only when the rewards declare weights (see pickWeightedIndex). The board
  // alone is edge-heavy — with five slots the middle one won ~15-17% of drops and the edges ~27%
  // each; steering moves the odds without changing how the drop looks.
  //   gain/maxVx: how fast, and how hard, the ball's sideways speed chases the slot
  //   minTime:    floor on the "time left to fall" estimate, so the first frames cannot over-steer
  //   bias:       how far toward the drawn slot the ball is released (0 = always the centre line)
  var STEER = {
    gain: 9,
    maxVx: 190,
    minTime: 0.16,
    bias: 0.35
  };

  var STORE_KEY = {
    played: 'lucky_game:played',
    claimed: 'lucky_game:claimed',
    dismissed: 'lucky_game:dismissed',
    // Marks the "already claimed" notice as read: written as soon as a claim completes, so a reload
    // after the sent screen does not raise the modal again, and written when the notice is raised
    // for a returning browser, so it is not shown on every page view.
    claimedSeen: 'lucky_game:claimed-seen',
    // Session-scoped, unlike the rest: the popup opens itself once per visit, so walking from the
    // homepage to a product page does not slam it shut in front of the guest again. Closing it in the
    // same visit does not arm it either — a refresh mid-visit must not bring it back.
    autoOpened: 'lucky_game:auto-opened'
  };

  // Klaviyo metric a flow listens to; it carries the stable voucher id a flow branches on.
  var KLAVIYO_EVENT = 'lucky_game_voucher_claimed';

  // Pinned API revision, shared by the event and subscription calls.
  var KLAVIYO_REVISION = '2025-10-15';

  // Session storage, for the auto-open marker. Kept apart from the localStorage helpers because the
  // failure mode differs: when the browser refuses session storage the marker lives in this object
  // instead, which still stops the popup from re-opening inside the current page view.
  var sessionBlocked = false;
  var sessionFallback = {};

  var roots = document.querySelectorAll('[data-ux-lucky-game]');

  for (var i = 0; i < roots.length; i++) {
    init(roots[i]);
  }

  function init(root) {
    var canvas = root.querySelector('[data-ux-lucky-game-canvas]');
    if (!canvas || !canvas.getContext) return;

    var ctx = canvas.getContext('2d');
    // One button per layout: the desktop card keeps its own in the accent panel, the stacked card
    // carries a second one under the board (see the mobile block in the stylesheet). Only one is ever
    // displayed, so both are driven together — labels, locked state and the click listener.
    var dropBtns = root.querySelectorAll('[data-ux-lucky-game-drop]');
    var dropBtn = dropBtns.length ? dropBtns[0] : null;
    var resultEl = root.querySelector('[data-ux-lucky-game-result]');
    var gridEl = root.querySelector('[data-ux-lucky-game-rewards-grid]');
    var stickyBtn = root.querySelector('[data-ux-lucky-game-sticky]');
    var stickyBar = root.querySelector('.ux-lucky-game-sticky');
    var stickyDismiss = root.querySelector('[data-ux-lucky-game-sticky-dismiss]');
    var stickyLabel = root.querySelector('[data-ux-lucky-game-sticky-label]');
    var stickyBadge = root.querySelector('[data-ux-lucky-game-sticky-badge]');
    var modal = root.querySelector('[data-ux-lucky-game-modal]');
    var dialog = root.querySelector('[data-ux-lucky-game-dialog]');
    var backdrop = root.querySelector('[data-ux-lucky-game-backdrop]');
    // The launch button is opt-in: Liquid only renders it when the merchant turns it on, so its
    // absence is what keeps syncSticky from ever revealing the wrapper.
    var stickyEnabled = attr(root, 'data-ux-lucky-game-sticky') === '1';
    var form = resolveForm(root);
    var emailInput = form ? form.querySelector('[data-ux-lucky-game-email]') : null;
    var consentInput = form ? form.querySelector('[data-ux-lucky-game-consent]') : null;
    var whatsappInput = form ? form.querySelector('[data-ux-lucky-game-whatsapp]') : null;
    var dialInput = form ? form.querySelector('[data-ux-lucky-game-dial]') : null;
    var errorEl = root.querySelector('[data-ux-lucky-game-error]');

    var klaviyoKey = attr(root, 'data-ux-lucky-game-klaviyo');
    var subscribeEnabled = attr(root, 'data-ux-lucky-game-subscribe') === '1';
    var subscribeListId = attr(root, 'data-ux-lucky-game-list');
    var subscribeSource = attr(root, 'data-ux-lucky-game-source') || 'lucky_game';
    var oncePerBrowser = attr(root, 'data-ux-lucky-game-once') !== '0';
    // A replayable game must stay reachable, so hiding only applies to the one-drop lock.
    var hideStickyAfterPlay = oncePerBrowser && attr(root, 'data-ux-lucky-game-sticky-hide') !== '0';
    // Seconds before the popup opens itself. 0 keeps Liquid's default; a typo or an empty attribute
    // falls back to 30s rather than to "never".
    var openDelaySetting = parseFloat(attr(root, 'data-ux-lucky-game-open-delay'));
    var openDelay = isFinite(openDelaySetting) && openDelaySetting >= 0 ? openDelaySetting : 30;
    // The launch button is hidden until something asks for it: a session that has already seen the
    // popup, a guest who closed it before claiming, or a play that leaves the reward unclaimed.
    var showSticky = false;
    // Armed once the popup has opened itself this visit: read from sessionStorage so a refresh inside
    // the same visit does not reopen it.
    var sessionOpen = !!readSession(STORE_KEY.autoOpened);
    var dropLabel = dropBtn ? dropBtn.textContent.replace(/\s+/g, ' ').trim() : '';
    var dropLockedLabel = dropBtn ? attr(dropBtn, 'data-ux-lucky-game-label-locked') || dropLabel : '';
    var stickyLabelText = stickyLabel ? stickyLabel.textContent.trim() : '';
    var stickyClaimedLabel = stickyBtn
      ? attr(stickyBtn, 'data-ux-lucky-game-label-claimed') || stickyLabelText
      : '';

    var declared = readRewards(root);
    var rewards = (declared && declared.items) || DEFAULT_REWARDS;
    // Any declared weight switches the board from "whatever physics decides" to "whatever the odds
    // promise": dropBall draws the slot and steerBall walks the ball into it.
    var weightedOdds = !!(declared && declared.weighted);
    var pegs = buildPegs();

    // Board inks come from the stylesheet, so the pegs follow the active colour scheme.
    var rootStyles = window.getComputedStyle(root);
    var pegColor = (rootStyles.getPropertyValue('--ux-lucky-game-peg-color') || '').trim() || '#262626';
    var ballColor = (rootStyles.getPropertyValue('--ux-lucky-game-ball-color') || '').trim() || '#ff111c';

    var ball = null;
    var animationId = null;
    var lastTime = 0;
    var accumulator = 0;
    // Stall watchdog: if the ball stops making downward progress while bouncing, the contact is a
    // dead-centre one the deflection floor could not resolve. Counted in steps, so it is frame-rate
    // independent; ~1.5s of no progress at the fixed step.
    var stallY = 0;
    var stallSteps = 0;
    var STALL_LIMIT = 1080;
    // Set by the first peg contact of a drop, so the apex nudge is applied once per drop.
    var apexContacted = false;
    var sessionReward = null;
    // Set for one drop when the odds are weighted: the drawn slot index and the x to land in it.
    var targetIndex = -1;
    var steerTarget = null;
    var lastFocus = null;

    renderRewards();
    sizeCanvas();

    // ------------------------------------------------------------------ board

    function buildPegs() {
      var list = [];

      for (var row = 0; row < BOARD.rows; row++) {
        var pegCount = row + 2;
        var totalWidth = (pegCount - 1) * BOARD.pegGap;
        var startX = BOARD.width / 2 - totalWidth / 2;
        var y = BOARD.startY + row * BOARD.rowGap;

        for (var j = 0; j < pegCount; j++) {
          list.push({ x: startX + j * BOARD.pegGap, y: y });
        }
      }

      return list;
    }

    function renderRewards() {
      if (!gridEl || gridEl.children.length) return;

      gridEl.style.setProperty('--ux-lucky-game-slots', String(rewards.length));

      for (var j = 0; j < rewards.length; j++) {
        var item = document.createElement('div');
        item.className = 'reward-item';

        var label = document.createElement('span');
        label.textContent = rewards[j].label;

        item.appendChild(label);
        gridEl.appendChild(item);
      }
    }

    function sizeCanvas() {
      var dpr = Math.max(1, window.devicePixelRatio || 1);

      canvas.width = BOARD.width * dpr;
      canvas.height = BOARD.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function drawPeg(peg) {
      ctx.beginPath();
      ctx.arc(peg.x, peg.y, BOARD.pegRadius, 0, Math.PI * 2);
      ctx.fillStyle = pegColor;
      ctx.fill();
    }

    function drawBall() {
      if (!ball) return;

      ctx.beginPath();
      ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
      ctx.fillStyle = ballColor;
      ctx.fill();
    }

    function drawBoard() {
      ctx.clearRect(0, 0, BOARD.width, BOARD.height);

      for (var j = 0; j < pegs.length; j++) {
        drawPeg(pegs[j]);
      }

      drawBall();
    }

    // -------------------------------------------------------------- physics

    function resetBall() {
      // Released slightly toward the drawn slot, the way a guest would pick a drop column, so the
      // servo only corrects the rest: saturating the sideways speed went from ~2% of flights to 0%.
      // It still drops from the middle when the board has no weighted odds (steerTarget is null).
      var startX = BOARD.width / 2;

      if (steerTarget) {
        startX += (steerTarget.x - startX) * STEER.bias;
      }

      ball = {
        x: startX + (Math.random() - 0.5) * 1.5,
        y: -10,
        radius: 7,
        vx: (Math.random() - 0.5) * 10,
        vy: 20,
        settledFrames: 0
      };

      lastTime = 0;
      accumulator = 0;
      stallY = ball.y;
      stallSteps = 0;
      apexContacted = false;
    }

    function clampSpeed() {
      var speed = Math.hypot(ball.vx, ball.vy);

      if (speed > PHYSICS.maxSpeed) {
        var ratio = PHYSICS.maxSpeed / speed;
        ball.vx *= ratio;
        ball.vy *= ratio;
      }
    }

    function resolvePegCollision(peg) {
      var dx = ball.x - peg.x;
      var dy = ball.y - peg.y;
      var minDistance = ball.radius + BOARD.pegRadius;
      var distSq = dx * dx + dy * dy;

      if (distSq >= minDistance * minDistance) return false;

      var distance = Math.sqrt(distSq);

      if (distance < 0.0001) {
        distance = 0.0001;
      }

      var nx = dx / distance;
      var ny = dy / distance;

      var penetration = minDistance - distance;
      ball.x += nx * penetration;
      ball.y += ny * penetration;

      // First contact of a drop: the ball arrives exactly between the two apex pegs, so both are
      // equidistant and the loop order picked the winner (100% left). Offsetting the contact point
      // turns that into a coin toss.
      if (!apexContacted) {
        apexContacted = true;
        ball.x += (Math.random() < 0.5 ? -1 : 1) * CONTACT.apexNudge;
      }

      var normalVelocity = ball.vx * nx + ball.vy * ny;

      // Only reflect when the ball is moving into the peg.
      if (normalVelocity < 0) {
        var tx = -ny;
        var ty = nx;
        var tangentVelocity = ball.vx * tx + ball.vy * ty;
        var newNormalVelocity = -normalVelocity * PHYSICS.restitution;
        var newTangentVelocity = tangentVelocity * PHYSICS.friction;

        ball.vx = newNormalVelocity * nx + newTangentVelocity * tx;
        ball.vy = newNormalVelocity * ny + newTangentVelocity * ty;

        ball.vx += (Math.random() - 0.5) * 2.0;

        // A near-vertical impact leaves vx at ~0, which parks the ball on the peg: guarantee a
        // sideways component so it keeps running down the board. Slow contacts are left alone, so a
        // ball settling into a channel is not flung sideways.
        if (Math.abs(ball.vx) < CONTACT.minDeflect && -normalVelocity > CONTACT.minNormalSpeed) {
          ball.vx = (ball.vx < 0 ? -1 : 1) * (CONTACT.minDeflect + Math.random() * CONTACT.minDeflect);
        }
      }

      return true;
    }

    // Draws the landing slot from the declared weights: 0 never wins, the rest share the odds in
    // proportion. Returns -1 when every weight is 0, which falls back to the physics.
    function pickWeightedIndex() {
      var total = 0;
      var j;

      for (j = 0; j < rewards.length; j++) {
        total += rewards[j].weight || 0;
      }

      if (!(total > 0)) return -1;

      var roll = Math.random() * total;

      for (j = 0; j < rewards.length; j++) {
        roll -= rewards[j].weight || 0;

        if (roll < 0) return j;
      }

      return rewards.length - 1;
    }

    // Pulls the ball toward the drawn slot's centre. The sideways speed is capped and the ball is
    // never teleported, so the guest still sees a bounce that went its way. Measured over 8 000
    // offline drops this reaches the drawn slot 99.99% of the time; finishDrop() keeps the drawn
    // slot either way, so the promised odds hold even on that one drop.
    function steerBall(dt) {
      if (!steerTarget) return;

      var dx = steerTarget.x - ball.x;
      var fall = Math.max(0, BOARD.height - ball.y);
      var timeLeft = Math.max(STEER.minTime, Math.sqrt((2 * fall) / PHYSICS.gravity));
      var desired = Math.max(-STEER.maxVx, Math.min(STEER.maxVx, dx / timeLeft));

      ball.vx += (desired - ball.vx) * Math.min(1, STEER.gain * dt);
    }

    function physicsStep(dt) {
      if (!ball) return;

      ball.vy += PHYSICS.gravity * dt;

      var drag = Math.pow(PHYSICS.airDrag, dt * 60);
      ball.vx *= drag;
      ball.vy *= drag;

      clampSpeed();
      steerBall(dt);

      ball.x += ball.vx * dt;
      ball.y += ball.vy * dt;

      for (var j = 0; j < pegs.length; j++) {
        resolvePegCollision(pegs[j]);
      }

      if (ball.x < ball.radius) {
        ball.x = ball.radius;

        if (ball.vx < 0) {
          ball.vx = -ball.vx * PHYSICS.wallRestitution;
        }
      }

      if (ball.x > BOARD.width - ball.radius) {
        ball.x = BOARD.width - ball.radius;

        if (ball.vx > 0) {
          ball.vx = -ball.vx * PHYSICS.wallRestitution;
        }
      }

      // Watchdog: break a contact the ball is not escaping from, rather than letting the drop hang.
      if (ball.y - stallY > 2) {
        stallY = ball.y;
        stallSteps = 0;
      } else if (++stallSteps > STALL_LIMIT) {
        ball.vx += ball.vx >= 0 ? CONTACT.minDeflect * 2 : -CONTACT.minDeflect * 2;
        stallSteps = 0;
      }

      if (ball.y >= BOARD.height - ball.radius) {
        ball.y = BOARD.height - ball.radius;
        finishDrop();
      }
    }

    function animate(timestamp) {
      if (!ball) return;

      if (!lastTime) {
        lastTime = timestamp;
      }

      // Cap the frame delta so a laggy tab cannot make the ball tunnel through pegs.
      var frameDt = Math.min((timestamp - lastTime) / 1000, 0.033);
      lastTime = timestamp;
      accumulator += frameDt;

      while (accumulator >= PHYSICS.fixedStep && ball) {
        physicsStep(PHYSICS.fixedStep);
        accumulator -= PHYSICS.fixedStep;
      }

      drawBoard();

      if (ball) {
        animationId = window.requestAnimationFrame(animate);
      }
    }

    // -------------------------------------------------------------- reward

    function dropBall() {
      if (!dropBtn || dropBtn.disabled) return;
      if (oncePerBrowser && read(STORE_KEY.played)) return;

      if (resultEl) resultEl.textContent = '';

      for (var dropIndex = 0; dropIndex < dropBtns.length; dropIndex++) {
        dropBtns[dropIndex].disabled = true;
      }

      // Draw the reward BEFORE the drop, so the odds are the configured ones and not the board's.
      steerTarget = null;
      targetIndex = -1;

      if (weightedOdds) {
        targetIndex = pickWeightedIndex();

        if (targetIndex >= 0) {
          steerTarget = { x: (targetIndex + 0.5) * (BOARD.width / rewards.length) };
        }
      }

      resetBall();
      drawBoard();

      animationId = window.requestAnimationFrame(animate);
    }

    function finishDrop() {
      if (!ball) return;

      var slotWidth = BOARD.width / rewards.length;
      // With weighted odds the drawn slot wins even if the ball was knocked a slot over on the way
      // down: the guest is promised the rate, and the steering only misses about 1 drop in 8 000.
      var index = targetIndex >= 0 ? targetIndex : Math.floor(ball.x / slotWidth);
      index = Math.max(0, Math.min(rewards.length - 1, index));

      var reward = rewards[index];
      reward.at = Date.now();
      sessionReward = reward;

      steerTarget = null;
      targetIndex = -1;

      write(STORE_KEY.played, { key: reward.key, label: reward.label, at: reward.at });

      if (resultEl) resultEl.textContent = 'You won: ' + reward.label;

      window.cancelAnimationFrame(animationId);
      animationId = null;

      var finishedBall = ball;
      ball = null;

      drawBoard();

      ctx.beginPath();
      ctx.arc(finishedBall.x, BOARD.height - finishedBall.radius, finishedBall.radius, 0, Math.PI * 2);
      ctx.fillStyle = ballColor;
      ctx.fill();

      fillRewardLabels(reward);

      // Hiding right after the drop would also hide it behind the email step that follows.
      if (hideStickyAfterPlay) showSticky = false;

      syncSticky();
      push('lucky_game:won', reward);
      celebrate();

      window.setTimeout(function () {
        drawBoard();
        updateDropButton();

        // Hide the board (canvas, reward labels, drop button) and show the email step.
        if (modal && !modal.hidden && !read(STORE_KEY.claimed)) {
          setModalState('claim');

          if (emailInput) emailInput.focus({ preventScroll: true });
        }
      }, 700);
    }

    function activeReward() {
      if (sessionReward) return sessionReward;

      var played = read(STORE_KEY.played);
      return played && played.key ? { key: played.key, label: played.label, at: played.at } : null;
    }

    /*
     * Splits a label on its last space, the same rule the Liquid chips use: the amount ("AED 75",
     * "15%", "$100") and the last word ("off", "voucher"). Without a space the whole label is the
     * amount.
     */
    function splitLabel(label) {
      var text = String(label || '').trim();
      var cut = text.lastIndexOf(' ');

      return cut > 0 ? { main: text.slice(0, cut), suffix: text.slice(cut + 1) } : { main: text, suffix: '' };
    }

    // The condition line comes from the slot list, looked up by key, so a reward restored from storage
    // picks up the merchant's current wording.
    function noteFor(reward) {
      for (var j = 0; j < rewards.length; j++) {
        if (rewards[j].key === reward.key) return rewards[j].note || '';
      }

      return '';
    }

    function fillText(selector, text) {
      var nodes = root.querySelectorAll(selector);

      for (var j = 0; j < nodes.length; j++) nodes[j].textContent = text;
    }

    /*
     * Renders the won reward into every placeholder. The label is split (see splitLabel) so the
     * stylesheet has the amount and the remainder as separate hooks; the steps that show both draw
     * them as one run of text at one size. `title` keeps one plain-text copy of the label for
     * assistive tech and for anything reading the node's text.
     */
    function fillRewardLabels(reward) {
      var nodes = root.querySelectorAll('[data-ux-lucky-game-reward-label]');
      var split = reward ? splitLabel(reward.label) : { main: '', suffix: '' };

      fillText('[data-ux-lucky-game-reward-amount]', split.main);
      fillText('[data-ux-lucky-game-reward-note]', reward ? noteFor(reward) || split.suffix : '');

      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];

        node.textContent = '';
        node.removeAttribute('title');

        if (!reward) continue;

        var main = document.createElement('span');
        var suffix = split.suffix;

        main.setAttribute('data-ux-lucky-game-reward-main', '');
        main.textContent = split.main;
        node.appendChild(main);

        if (suffix) {
          var rest = document.createElement('span');
          rest.setAttribute('data-ux-lucky-game-reward-suffix', '');
          rest.textContent = suffix;
          node.appendChild(rest);
        }

        node.setAttribute('title', reward.label);
      }
    }

    function updateDropButton() {
      if (!dropBtn) return;

      var played = read(STORE_KEY.played);
      var locked = oncePerBrowser && !!played;
      var dropDisabled = locked || ball !== null;
      var dropText = locked ? dropLockedLabel : dropLabel;

      for (var dropIndex = 0; dropIndex < dropBtns.length; dropIndex++) {
        dropBtns[dropIndex].disabled = dropDisabled;
        dropBtns[dropIndex].textContent = dropText;
      }

      if (locked && !read(STORE_KEY.claimed) && resultEl && played) {
        resultEl.textContent = 'You won: ' + played.label;
      }
    }

    // Launch-button visibility. The wrapper starts hidden in the markup, so this only ever reveals
    // or re-hides it, and only when Liquid rendered the button at all. A guest who dismissed the chip
    // earlier, or who already claimed, never sees it.
    function stickyVisible() {
      if (!stickyEnabled) return false;
      if (read(STORE_KEY.dismissed)) return false;
      if (read(STORE_KEY.claimed)) return false;

      return showSticky;
    }

    function syncSticky() {
      if (!stickyBtn) return;

      var reward = activeReward();
      var claimed = !!read(STORE_KEY.claimed);

      if (stickyBar) stickyBar.hidden = !stickyVisible();

      if (stickyBadge) {
        stickyBadge.hidden = !reward;
        if (reward) stickyBadge.textContent = reward.label;
      }

      if (stickyLabel) {
        stickyLabel.textContent = claimed ? stickyClaimedLabel : stickyLabelText;
      }

      stickyBtn.setAttribute('data-state', claimed ? 'claimed' : reward ? 'won' : 'idle');
    }

    // Remembered per browser.
    function dismissSticky() {
      write(STORE_KEY.dismissed, { at: Date.now() });
      syncSticky();

      push('lucky_game:dismissed', activeReward() || { key: '', label: '' });

      if (stickyDismiss && document.activeElement === stickyDismiss) stickyDismiss.blur();
    }

    // ---------------------------------------------------------- celebration

    // Light firework burst when the ball lands: plain DOM + one CSS animation, created once and
    // removed after it has played. Nothing is created under prefers-reduced-motion.
    function prefersReducedMotion() {
      return !!(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    function celebrate() {
      if (!dialog || !modal || modal.hidden || prefersReducedMotion()) return;

      var colors = [ballColor, '#ffc94d', '#ffffff'];
      var count = 32;
      var layer = document.createElement('div');

      layer.className = 'ux-lucky-game-burst';
      layer.setAttribute('aria-hidden', 'true');

      for (var j = 0; j < count; j++) {
        var angle = (j / count) * Math.PI * 2 + Math.random() * 0.4;
        var speed = 10 + Math.random() * 12;
        var square = Math.random() < 0.35;
        var dot = document.createElement('span');

        dot.className = 'ux-lucky-game-burst__dot';
        dot.style.left = (44 + Math.random() * 12).toFixed(2) + '%';
        dot.style.top = (34 + Math.random() * 8).toFixed(2) + '%';
        dot.style.setProperty('--ux-lucky-game-dot-dx', (Math.cos(angle) * speed).toFixed(1) + 'rem');
        dot.style.setProperty(
          '--ux-lucky-game-dot-dy',
          (Math.sin(angle) * speed * 0.8 - 3).toFixed(1) + 'rem'
        );
        dot.style.setProperty(
          '--ux-lucky-game-dot-rot',
          Math.round((Math.random() - 0.5) * 540) + 'deg'
        );
        dot.style.setProperty(
          '--ux-lucky-game-dot-size',
          (0.6 + Math.random() * 0.55).toFixed(2) + 'rem'
        );
        dot.style.setProperty('--ux-lucky-game-dot-radius', square ? '0.15rem' : '50%');
        dot.style.setProperty(
          '--ux-lucky-game-dot-delay',
          (Math.random() * 0.12).toFixed(2) + 's'
        );
        dot.style.setProperty('--ux-lucky-game-dot-color', colors[j % colors.length]);

        layer.appendChild(dot);
      }

      dialog.appendChild(layer);

      window.setTimeout(function () {
        if (layer.parentNode) layer.parentNode.removeChild(layer);
      }, 1900);
    }

    // --------------------------------------------------------------- popup

    function currentState() {
      if (read(STORE_KEY.claimed)) return 'claimed';
      return activeReward() ? 'claim' : 'game';
    }

    function setModalState(state) {
      if (!modal) return;

      modal.setAttribute('data-ux-lucky-game-state', state);

      var panels = modal.querySelectorAll('[data-ux-lucky-game-panel]');

      for (var j = 0; j < panels.length; j++) {
        panels[j].hidden = panels[j].getAttribute('data-ux-lucky-game-panel') !== state;
      }
    }

    // Page scroll lock. `overflow: hidden` removes the scrollbar, and where that bar takes layout
    // space the page behind would widen ~15px and every box would jump for a frame. So: (1) browsers
    // with `scrollbar-gutter` reserve the space for the duration of the lock, otherwise (2) the
    // measured width comes back as body padding. The two never stack, because (1) also takes the
    // measurement to 0: once the gutter is reserved the client width no longer differs.
    var supportsScrollbarGutter =
      typeof window.CSS !== 'undefined' &&
      typeof window.CSS.supports === 'function' &&
      window.CSS.supports('scrollbar-gutter', 'stable');

    function lockPageScroll() {
      var gap = window.innerWidth - document.documentElement.clientWidth;
      var body = document.body;
      var useGutter = supportsScrollbarGutter && gap > 0;

      if (useGutter) {
        document.documentElement.classList.add('ux-lucky-game-scroll-locked');
      }

      body.style.setProperty('--ux-lucky-game-scrollbar-gap', (useGutter ? 0 : Math.max(gap, 0)) + 'px');
      body.classList.add('ux-lucky-game-modal-open', 'overflow-hidden');
    }

    function unlockPageScroll() {
      var body = document.body;

      document.documentElement.classList.remove('ux-lucky-game-scroll-locked');
      body.classList.remove('ux-lucky-game-modal-open', 'overflow-hidden');
      body.style.removeProperty('--ux-lucky-game-scrollbar-gap');
    }

    function openModal(state) {
      if (!modal) return;

      lastFocus = document.activeElement;
      setModalState(state || currentState());

      modal.hidden = false;
      lockPageScroll();

      if (dialog) dialog.focus({ preventScroll: true });
    }

    function closeModal() {
      if (!modal || modal.hidden) return;

      modal.hidden = true;
      unlockPageScroll();

      // Closed before claiming: leave the launch button as the way back in, or the reward the guest
      // just won has no route to the email step. Only when the merchant kept the button on.
      if (!read(STORE_KEY.claimed) && !read(STORE_KEY.dismissed)) {
        showSticky = true;
        syncSticky();
      }

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    function trapFocus(event) {
      if (event.key !== 'Tab' || !dialog) return;

      var focusable = dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var visible = [];

      for (var j = 0; j < focusable.length; j++) {
        if (focusable[j].offsetParent !== null) visible.push(focusable[j]);
      }

      if (!visible.length) return;

      var first = visible[0];
      var last = visible[visible.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // ------------------------------------------------------ voucher + Klaviyo

    // The winning slot as a payload downstream systems can key off. `voucher` is the stable id
    // (`voucher_<key>`), safe for a Klaviyo split, a GA4 item id or a dataLayer field.
    function voucherOf(reward) {
      if (!reward || !reward.key) return null;

      return {
        key: reward.key,
        label: reward.label || reward.key,
        voucher: 'voucher_' + reward.key
      };
    }

    // Tells Klaviyo which voucher this profile won: an explicit email profile, voucher properties
    // and its own metric event, with no Shopify request involved. Profile properties persist
    // (voucher_key / voucher_label / voucher_id, lucky_game_voucher_holder, lucky_game_claim_count,
    // lucky_game_last_claim_at); the event carries voucher_id / voucher_key / voucher_label / email
    // / source. Rejects on missing configuration or a refused event, so the guest can retry instead
    // of seeing a false success.
    function klaviyoTrack(voucher) {
      if (!klaviyoKey) return Promise.reject(new Error('Klaviyo company ID is missing.'));
      if (!voucher || !voucher.key || !voucher.email) {
        return Promise.reject(new Error('Klaviyo claim is incomplete.'));
      }

      var properties = {
        voucher_id: voucher.voucher,
        voucher_key: voucher.key,
        voucher_label: voucher.label,
        source: 'lucky_game'
      };

      properties.email = voucher.email;
      if (voucher.whatsapp) properties.whatsapp_number = voucher.whatsapp;

      // Never call the onsite identify API here: a browser can still be cookied to a previous
      // profile, and an async identify racing this event would attach the claim to that old profile.
      return klaviyoPost(properties, voucher);
    }

    // Posts straight to Klaviyo's client API with the public key, independent of the onsite script
    // or the placeholder. Verified against the live endpoint: custom properties must NOT sit in
    // `profile.data.attributes` (400 "not a valid field for the resource 'profile'") but in
    // `profile.data.attributes.properties`; only email + properties are sent, and marketing consent
    // is deliberately not claimed here. The referrer must be allowed and it is rate-limited per
    // profile, but a rejected request still has to be accepted before the claim shows as successful.
    function klaviyoPost(properties, voucher) {
      if (!window.fetch || typeof window.fetch !== 'function') {
        return Promise.reject(new Error('Fetch is not supported.'));
      }

      var attributes = {
        metric: { data: { type: 'metric', attributes: { name: KLAVIYO_EVENT } } },
        properties: properties,
        time: new Date().toISOString(),
        unique_id: KLAVIYO_EVENT + ':' + properties.voucher_id + ':' + voucher.at
      };

      if (voucher.email) {
        var profileProperties = {
          voucher_key: voucher.key,
          voucher_label: voucher.label,
          voucher_id: voucher.voucher,
          lucky_game_voucher_holder: true,
          lucky_game_claim_count: voucher.count || 1,
          lucky_game_last_claim_at: new Date(voucher.at || Date.now()).toISOString()
        };

        // A plain custom property, not `phone_number`: Klaviyo rejects the whole request on a phone it
        // cannot parse, and an optional field must never cost the guest the voucher. The standard
        // field is set afterwards by klaviyoSavePhone.
        if (voucher.whatsapp) profileProperties.whatsapp_number = voucher.whatsapp;

        attributes.profile = {
          data: {
            type: 'profile',
            attributes: {
              email: voucher.email,
              properties: profileProperties
            }
          }
        };
      }

      var payload = { data: { type: 'event', attributes: attributes } };

      return window
        .fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(klaviyoKey), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload),
          keepalive: true
        })
        .then(function (response) {
          if (!response.ok) throw new Error('Klaviyo event rejected (' + response.status + ').');
          return response;
        });
    }

    // The other half of the email step: the same address joins a Klaviyo list via the client-side
    // subscriptions endpoint (public key only). Consent is stated explicitly
    // (`subscriptions.email.marketing.consent = SUBSCRIBED`) rather than left to the endpoint's
    // default, so the record says only what the guest agreed to; `custom_source` stamps where it
    // came from. 202 Accepted means queued, anything else is a rejected signup, which the caller
    // treats as non-fatal — see onSubmit.
    function klaviyoSubscribe(voucher) {
      if (!klaviyoKey) return Promise.reject(new Error('Klaviyo company ID is missing.'));
      if (!subscribeListId) return Promise.reject(new Error('Klaviyo list ID is missing.'));
      if (!voucher || !voucher.email) {
        return Promise.reject(new Error('Klaviyo signup is incomplete.'));
      }

      if (!window.fetch || typeof window.fetch !== 'function') {
        return Promise.reject(new Error('Fetch is not supported.'));
      }

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: subscribeSource,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: voucher.email,
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } }
                  }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: subscribeListId } }
          }
        }
      };

      return window
        .fetch(
          'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(klaviyoKey),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              revision: KLAVIYO_REVISION
            },
            body: JSON.stringify(payload),
            keepalive: true
          }
        )
        .then(function (response) {
          if (!response.ok) throw new Error('Klaviyo signup rejected (' + response.status + ').');
          return response;
        });
    }

    // Copies a parsed WhatsApp number onto the profile's standard `phone_number`, on its own request
    // and only after the claim has landed, so a number Klaviyo refuses (or one already on another
    // profile) costs nothing but this field. Only the number is sent: no SMS or WhatsApp consent.
    function klaviyoSavePhone(voucher) {
      if (!klaviyoKey || !voucher || !voucher.email || !voucher.phone) return Promise.resolve();
      if (!window.fetch || typeof window.fetch !== 'function') return Promise.resolve();

      var payload = {
        data: {
          type: 'profile',
          attributes: { email: voucher.email, phone_number: voucher.phone }
        }
      };

      return window
        .fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(klaviyoKey), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload),
          keepalive: true
        })
        .then(function (response) {
          if (!response.ok) throw new Error('Klaviyo phone rejected (' + response.status + ').');
          return response;
        });
    }

    // E.164 from what the guest typed: "+…" or "00…" keeps its own country code, anything else is a
    // local number under the picked dial code (a trunk 0 dropped). A local number that already starts
    // with the dial code and would run past 13 digits with it added is read as already international
    // (971501234567 typed under +971). Returns '' unless 8–15 digits remain; Klaviyo checks the rest.
    function toE164(value, dial) {
      var raw = (value || '').trim();
      var digits = raw.replace(/\D/g, '');
      if (!digits) return '';

      var number;
      if (raw.charAt(0) === '+') {
        number = digits;
      } else if (digits.indexOf('00') === 0) {
        number = digits.slice(2);
      } else {
        if (!dial) return '';
        var local = digits.replace(/^0+/, '');
        number = local.indexOf(dial) === 0 && (dial + local).length > 13 ? local : dial + local;
      }

      return /^[1-9]\d{7,14}$/.test(number) ? '+' + number : '';
    }

    function normalizeEmail(value) {
      return (value || '').trim().toLowerCase();
    }

    function fieldValue(field) {
      return field && typeof field.value === 'string' ? field.value.trim() : '';
    }

    // "You already claimed." Two triggers, same notice: a new submit from an email this browser
    // already claimed with (told before the POST), and a cleared email field on a browser that
    // already holds a claim. Per browser (localStorage), like the one-play lock: cross-browser
    // dedupe would need a trusted server-side lookup the public Klaviyo API cannot provide.
    function showClaimedNotice(claim) {
      fillRewardLabels(claim);
      openModal('claimed');
    }

    function remindClaimed(claim) {
      if (!claim || !claim.key) return false;

      var seen = read(STORE_KEY.claimedSeen);
      if (seen && seen.key === claim.key) return false;

      write(STORE_KEY.claimedSeen, { key: claim.key, at: Date.now() });
      showClaimedNotice(claim);

      return true;
    }

    function checkEmail() {
      var claimed = read(STORE_KEY.claimed);
      var email = normalizeEmail(fieldValue(emailInput));

      if (!claimed || !claimed.key) return false;
      if (!claimed.email || email !== claimed.email) return false;

      showClaimedNotice(claimed);

      return true;
    }

    // Shown once per browser, then left alone; the launch button stays hidden (see stickyVisible).
    function maybeRemindClaimed() {
      var claimed = read(STORE_KEY.claimed);
      if (!claimed || !claimed.key) return;

      remindClaimed(claimed);
    }

    /*
     * Opens the popup by itself once the guest has stayed `openDelay` seconds on one page. Every page
     * starts its own clock. `sessionOpen` is only armed when the popup really opens, so a page left
     * before then just drops its timer and the next page starts over.
     *
     *   already opened this visit → do nothing (the guest closed it, or is mid-claim)
     *   already claimed           → the "already claimed" notice, which is itself once per browser
     *   otherwise                 → the game, or the email step for a reward won earlier
     */
    function scheduleAutoOpen() {
      if (!modal || sessionOpen) return;
      if (!modal.hasAttribute('hidden')) return;
      if (!(openDelay >= 0)) return;

      window.setTimeout(function () {
        if (sessionOpen) return;

        sessionOpen = true;
        writeSession(STORE_KEY.autoOpened, { at: Date.now() });
        autoOpen();
      }, openDelay * 1000);
    }

    function autoOpen() {
      if (!modal.hidden) return;

      var claimed = read(STORE_KEY.claimed);

      if (claimed && claimed.key) {
        maybeRemindClaimed();
        return;
      }

      openModal(currentState());
    }

    // -------------------------------------------------------------- submit

    function push(name, reward) {
      root.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: {
            key: reward.key,
            label: reward.label,
            voucher: reward.voucher || 'voucher_' + reward.key
          }
        })
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: name.replace(':', '_'),
        lucky_game_reward: reward.key,
        lucky_game_reward_label: reward.label,
        lucky_game_voucher: reward.voucher || 'voucher_' + reward.key
      });
    }

    function onSubmit(event) {
      event.preventDefault();

      var reward = activeReward();

      if (!reward) {
        openModal('game');
        return;
      }

      var claimed = read(STORE_KEY.claimed);
      var email = normalizeEmail(fieldValue(emailInput));

      if (!emailInput || !email || !emailInput.checkValidity()) {
        if (emailInput && typeof emailInput.reportValidity === 'function') emailInput.reportValidity();
        return;
      }

      // Same address, same browser: answer with the notice instead of posting a second voucher.
      if (claimed && claimed.key && claimed.email && email && email === claimed.email) {
        showClaimedNotice(claimed);
        return;
      }

      var voucher = voucherOf(reward);
      if (!voucher) return;

      voucher.email = email;
      voucher.phone = toE164(fieldValue(whatsappInput), dialInput ? dialInput.value : '');
      // The parsed number when there is one, so the property reads the same for every guest.
      voucher.whatsapp = voucher.phone || fieldValue(whatsappInput);
      voucher.count = ((claimed && claimed.count) || 0) + 1;
      voucher.at = reward.at || Date.now();

      push('lucky_game:claim', reward);
      setBusy(true);
      hideError();

      // The signup and the claim go out together but are deliberately NOT coupled: the event is what
      // the reward flow listens to, so a rejected signup must not turn a won voucher into an error.
      // Consent is a preference, not a gate: the box ships ticked and is not required, so unticking it
      // only takes the guest off the list, never off the voucher.
      var consentGiven = !consentInput || consentInput.checked;
      var subscribed = false;
      var signup = subscribeEnabled && consentGiven
        ? klaviyoSubscribe(voucher)
            .then(function () {
              subscribed = true;
            })
            .catch(function (error) {
              console.error('Lucky game: Klaviyo signup failed.', error);
            })
        : Promise.resolve();

      klaviyoTrack(voucher)
        .then(function () {
          // Not waited on: the phone field is a bonus, never part of the claim.
          klaviyoSavePhone(voucher).catch(function (error) {
            console.warn('Lucky game: Klaviyo phone not saved.', error);
          });
          return signup;
        })
        .then(function () {
          setBusy(false);
          completeClaim(voucher, subscribed);
        })
        .catch(function (error) {
          console.error('Lucky game: Klaviyo claim failed.', error);
          setBusy(false);
          showError('Could not send. Check your connection and try again.');
        });
    }

    // A claim becomes official only after Klaviyo accepts the profile + event request. Both records
    // land here, before the sent screen is shown: `claimed` locks the drop and hides the launch
    // button, `claimedSeen` keeps the "already claimed" notice from re-opening the modal on reload.
    // A guest who refreshes the sent screen therefore comes back to a quiet page.
    function completeClaim(voucher, subscribed) {
      voucher.subscribed = !!subscribed;

      write(STORE_KEY.claimed, voucher);
      write(STORE_KEY.claimedSeen, { key: voucher.key, at: Date.now() });

      fillRewardLabels(voucher);
      setModalState('success');
      syncSticky();
      updateDropButton();
      push('lucky_game:claimed', voucher);

      if (voucher.subscribed) push('lucky_game:subscribed', voucher);

      return voucher;
    }

    function setBusy(busy) {
      if (!form) return;

      if (busy) {
        form.setAttribute('data-ux-lucky-game-busy', '');
        form.setAttribute('aria-busy', 'true');
      } else {
        form.removeAttribute('data-ux-lucky-game-busy');
        form.removeAttribute('aria-busy');
      }

      var submit = form.querySelector('button[type="submit"]');
      if (submit) submit.disabled = busy;
    }

    function showError(message) {
      if (!errorEl) return;

      errorEl.textContent = message;
      errorEl.hidden = false;
      if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
    }

    function hideError() {
      if (!errorEl) return;

      errorEl.textContent = '';
      errorEl.hidden = true;
      if (emailInput) emailInput.removeAttribute('aria-invalid');
    }

    // ---------------------------------------------------------------- wire

    for (var dropIndex = 0; dropIndex < dropBtns.length; dropIndex++) {
      dropBtns[dropIndex].addEventListener('click', dropBall);
    }

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        openModal(currentState());
      });
    }

    if (stickyDismiss) stickyDismiss.addEventListener('click', dismissSticky);

    if (form) form.addEventListener('submit', onSubmit);

    // Say so on blur, before the guest bothers to submit.
    if (emailInput) emailInput.addEventListener('blur', checkEmail);

    if (backdrop) backdrop.addEventListener('click', closeModal);

    var closeNodes = root.querySelectorAll('[data-ux-lucky-game-close]');
    for (var c = 0; c < closeNodes.length; c++) {
      closeNodes[c].addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeModal();
    });

    if (modal) modal.addEventListener('keydown', trapFocus);

    fillRewardLabels(activeReward());
    syncSticky();
    updateDropButton();
    scheduleAutoOpen();

    drawBoard();
  }

  // ------------------------------------------------------------- helpers

  function attr(el, name) {
    return (el.getAttribute(name) || '').trim();
  }

  function resolveForm(root) {
    var host = root.querySelector('[data-ux-lucky-game-form]');
    if (!host) return null;

    return host.tagName === 'FORM' ? host : host.querySelector('form');
  }

  // Slot list from Liquid. `weight` is optional and only present when the merchant wrote a third
  // column (`key|Label|2`), so "no weights anywhere" (the board decides) stays distinguishable from
  // an explicit weight of 1. Declared weights are relative; slots without one count as 1.
  function readRewards(root) {
    var node = root.querySelector('script[data-ux-lucky-game-rewards-json]');
    if (!node) return null;

    try {
      var parsed = JSON.parse(node.textContent);
      if (!Array.isArray(parsed) || !parsed.length) return null;

      var items = [];
      var weighted = false;

      for (var j = 0; j < parsed.length; j++) {
        var key = String(parsed[j].key || '').trim();
        if (!key) continue;

        var raw = parsed[j].weight;
        var weight = typeof raw === 'number' && isFinite(raw) && raw >= 0 ? raw : null;

        if (weight !== null) weighted = true;

        items.push({
          key: key,
          label: String(parsed[j].label || key).trim(),
          weight: weight === null ? 1 : weight,
          note: String(parsed[j].note || '').trim()
        });
      }

      return items.length ? { items: items, weighted: weighted } : null;
    } catch (error) {
      return null;
    }
  }

  function read(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Private mode / storage disabled: the claim still went through.
    }
  }

  // Session storage, for the auto-open marker. The state lives next to the roots above.
  function readSession(key) {
    if (sessionBlocked) return Object.prototype.hasOwnProperty.call(sessionFallback, key) ? sessionFallback[key] : null;

    try {
      return JSON.parse(window.sessionStorage.getItem(key) || 'null');
    } catch (error) {
      sessionBlocked = true;
      return Object.prototype.hasOwnProperty.call(sessionFallback, key) ? sessionFallback[key] : null;
    }
  }

  function writeSession(key, value) {
    sessionFallback[key] = value;

    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      sessionBlocked = true;
    }
  }

})();
